/**
 * Flat certification requirements → tree, WELL-style metadata helpers.
 * Keeps legacy standards working when import_metadata is absent.
 *
 * Copy of the main app's client/src/utils/requirementTree.js (plan 18.3), with one change:
 * buildRequirementsTree's sort key. The main app sorts each level by `weight` alone, which
 * is fine there because every standard's weights are assigned to reflect display order. GRIHA
 * is seeded with weight = points (max_points for a root, an appraisal's own points for a
 * child - backend/seed_standards.py's _root_fields/_child_fields), and Table 6's point values
 * do not follow criterion order (e.g. C01 can outweigh C02), so a weight-only sort would
 * scramble the roots away from C01..C30. This copy sorts by `section` first and falls back to
 * `weight` only when two sections tie (never happens for GRIHA's own rows, but keeps the
 * comparator total for any future standard that reuses this tree builder).
 *
 * Section strings sort correctly with a plain `<`/`>` compare as long as every numeric run
 * being compared has the same width: GRIHA's roots are zero-padded ("C01".."C30", so "C09" <
 * "C10" byte-for-byte) and its appraisal children never exceed a single digit in their last
 * segment ("4.1.1", "4.1.2" - the detailed criteria in backend/app/data/griha_v6.json). It
 * would NOT hold for an unpadded double-digit child, e.g. "4.1.10" vs "4.1.2": as plain
 * strings "4.1.10" sorts before "4.1.2" because '1' < '2' at the first differing character,
 * even though 10 > 2 numerically. requirementTree.test.js pins both the correct GRIHA-shaped case and this
 * documented limitation so a future standard that needs >9 children per section knows to add
 * a numeric-aware comparator instead of assuming this one generalizes.
 */

export function buildRequirementsTree(requirements) {
  if (!requirements || requirements.length === 0) return [];

  const map = new Map();
  requirements.forEach((req) => {
    map.set(req.id, { ...req, children: [] });
  });

  const roots = [];
  requirements.forEach((req) => {
    const node = map.get(req.id);
    if (req.parent_id && map.has(req.parent_id)) {
      map.get(req.parent_id).children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortBySection = (nodes) => {
    nodes.sort((a, b) => {
      const sa = String(a.section ?? '');
      const sb = String(b.section ?? '');
      if (sa < sb) return -1;
      if (sa > sb) return 1;
      return (a.weight || 0) - (b.weight || 0);
    });
    nodes.forEach((n) => {
      if (n.children?.length) sortBySection(n.children);
    });
  };
  sortBySection(roots);
  return roots;
}

/**
 * Merged metadata for UI: supports camelCase API, legacy requirement_metadata,
 * and top-level `points` / `point` on the requirement object.
 */
export function getImportMeta(node) {
  if (!node || typeof node !== 'object') return {};
  const legacy = node.requirement_metadata ?? node.requirementMetadata;
  const primary = node.import_metadata ?? node.importMetadata;
  const out = {
    ...(legacy && typeof legacy === 'object' ? legacy : {}),
    ...(primary && typeof primary === 'object' ? primary : {}),
  };
  const topPoints = node.points ?? node.point;
  if (topPoints != null && topPoints !== '' && out.points == null) {
    out.points = topPoints;
  }
  return out;
}

/** Raw points value for display (WELL number or { well_certification, well_core }). */
export function getPointsForDisplay(node) {
  const m = getImportMeta(node);
  const hasParent = !!(node?.parent_id || node?.parentId);

  if (m.points !== undefined && m.points !== null && m.points !== '') {
    if (typeof m.points === 'number' && m.points === 0 && hasParent) {
      // Child rows often carry points: 0 as a placeholder; prefer weight below when set.
    } else if (typeof m.points === 'object' && hasParent && Object.keys(m.points).length === 0) {
      // Empty points object on children — fall through to weight.
    } else {
      return m.points;
    }
  }
  if (m.point !== undefined && m.point !== null && m.point !== '') {
    if (typeof m.point === 'number' && m.point === 0 && hasParent) {
      // same as m.points for children
    } else {
      return m.point;
    }
  }
  const t = String(m.type || '').toUpperCase();
  const w = node?.weight;
  if (w != null && Number(w) > 0 && t === 'OPTIMIZATION') {
    return Number(w);
  }
  // LEED-style options/paths: children carry achievable credit weight without WELL "type".
  if (hasParent && w != null && Number(w) > 0) {
    return Number(w);
  }
  return null;
}

export function getComplianceType(node) {
  const ct = getImportMeta(node).compliance_type;
  if (!ct || typeof ct !== 'string') return null;
  return ct.trim().toUpperCase();
}

/** Compliance chip text for UI; hide AND-only labels (selection logic still uses full type). */
export function getComplianceTypeForDisplay(node) {
  const ct = getComplianceType(node);
  if (ct === 'AND') return null;
  return ct;
}

export function hasStructuredImportMetadata(flatRequirements) {
  if (!flatRequirements?.length) return false;
  return flatRequirements.some((r) => {
    const m = getImportMeta(r);
    return m && typeof m === 'object' && Object.keys(m).length > 0;
  });
}

/**
 * Human-readable points for UI (prefers document "points" over internal weight).
 */
export function formatPointsDisplay(points) {
  if (points === null || points === undefined || points === '') return null;
  if (typeof points === 'string') {
    const n = Number(points.trim());
    if (!Number.isNaN(n) && points.trim() !== '') return formatPointsDisplay(n);
    return null;
  }
  if (typeof points === 'number') {
    if (points === 0) return '0 pts';
    return `${points} pt${points === 1 ? '' : 's'}`;
  }
  if (typeof points === 'object') {
    if (Object.keys(points).length === 0) return null;
    const wc = points.well_certification;
    const wcore = points.well_core;
    if (wc != null && wcore != null) {
      return `Cert ${wc} / Core ${wcore} pts`;
    }
    if (wc != null) return `${wc} pt${Number(wc) === 1 ? '' : 's'} (Cert)`;
    if (wcore != null) return `${wcore} pt${Number(wcore) === 1 ? '' : 's'} (Core)`;
  }
  return null;
}

export function getAllLeafRequirementIds(flatRequirements) {
  if (!flatRequirements?.length) return [];
  const hasChildren = new Set();
  flatRequirements.forEach((r) => {
    if (r.parent_id) hasChildren.add(r.parent_id);
  });
  return flatRequirements.filter((r) => !hasChildren.has(r.id)).map((r) => r.id);
}

export function collectLeafIdsUnder(node) {
  if (!node.children?.length) return [node.id];
  return node.children.flatMap((c) => collectLeafIdsUnder(c));
}

function unionLeafSets(nodes) {
  const out = new Set();
  nodes.forEach((n) => {
    collectLeafIdsUnder(n).forEach((id) => out.add(id));
  });
  return out;
}

function removeIds(set, ids) {
  ids.forEach((id) => set.delete(id));
}

/**
 * @param {Set<string>} selected
 * @param {object} node - tree node with children
 * @param {'OR'|'AND'} groupType - parent's compliance_type for these children
 * @param {object} parentNode - parent tree node
 */
export function toggleChildInGroup(selected, node, groupType, parentNode) {
  const siblings = parentNode.children || [];
  const nodeLeaves = collectLeafIdsUnder(node);
  const allSiblingLeaves = unionLeafSets(siblings);

  if (groupType === 'OR') {
    removeIds(selected, [...allSiblingLeaves]);
    nodeLeaves.forEach((id) => selected.add(id));
    return;
  }

  if (groupType === 'AND') {
    const allSelected = [...allSiblingLeaves].every((id) => selected.has(id));
    if (allSelected) {
      removeIds(selected, [...allSiblingLeaves]);
    } else {
      allSiblingLeaves.forEach((id) => selected.add(id));
    }
  }
}

/**
 * Toggle a single requirement id (legacy / non-group leaves).
 */
export function toggleId(selected, id) {
  if (selected.has(id)) selected.delete(id);
  else selected.add(id);
}

export function getRootRequirement(flatRequirements, requirementId) {
  if (!flatRequirements?.length || !requirementId) return null;
  const byId = new Map(flatRequirements.map((r) => [r.id, r]));
  let cur = byId.get(requirementId);
  if (!cur) return null;
  while (cur.parent_id && byId.has(cur.parent_id)) {
    cur = byId.get(cur.parent_id);
  }
  return cur;
}

export function groupSelectedByRoot(flatRequirements, selectedIds) {
  if (!flatRequirements?.length || !selectedIds?.length) return [];
  const byRoot = new Map();
  selectedIds.forEach((id) => {
    const root = getRootRequirement(flatRequirements, id);
    if (!root) return;
    if (!byRoot.has(root.id)) {
      byRoot.set(root.id, { root, selectedIds: [] });
    }
    const group = byRoot.get(root.id);
    if (!group.selectedIds.includes(id)) group.selectedIds.push(id);
  });
  return Array.from(byRoot.values());
}

export function getSelectedPathLabels(flatRequirements, rootId, selectedIds) {
  if (!flatRequirements?.length || !selectedIds?.length) return [];
  const byId = new Map(flatRequirements.map((r) => [r.id, r]));
  return selectedIds
    .filter((id) => {
      const root = getRootRequirement(flatRequirements, id);
      return root?.id === rootId;
    })
    .map((id) => byId.get(id)?.title || id)
    .filter(Boolean);
}
